import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Flex, Modal, P } from '@decisiv/ui-components';
import Download from '@decisiv/iconix/lib/components/Download';
import Times from '@decisiv/iconix/lib/components/Times';
import { t, Trans } from '@lingui/macro';
import { fileStamp } from '../../utils/dates';

import WrapperWithLoading from '../WrapperWithLoading';
import IntegrationsApi from '../../api/integrations';
import { IntegrationRow, RemoveButton } from './style';

export default function ExportIntegrationsModal({
  visible,
  onClose,
  integrations = [],
  onRemove,
  onExported,
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  // The component stays mounted across open/close cycles (only `visible`
  // toggles) — clear a prior failed/in-flight attempt each time it reopens.
  useEffect(() => {
    if (visible) {
      setError(null);
      setDownloading(false);
    }
  }, [visible]);

  const handleDownload = async () => {
    setError(null);
    setDownloading(true);

    const uuids = integrations.map(({ id }) => id);
    const { data: bundle, errors } = await IntegrationsApi.exportIntegrations(
      uuids,
    );

    if (errors || !bundle) {
      // A specific server message (e.g. a batch-size limit) explains itself
      // better than a generic fallback — only fall back when there isn't one.
      setError(
        errors?.[0]?.detail ||
          t`Something went wrong exporting these integrations. Please try again.`,
      );
      setDownloading(false);
      return;
    }

    const blob = new Blob([JSON.stringify(bundle, null, 2)], {
      type: 'application/json',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      bundle?.meta?.exported_from
        ? `integrations-export-${bundle.meta.exported_from}-${fileStamp()}.json`
        : 'integrations-export.json',
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    setDownloading(false);
    onExported();
  };

  return (
    <Modal
      actions={[
        {
          text: t`Download Export File`,
          onClick: handleDownload,
          disabled: downloading || integrations.length === 0,
        },
        { text: t`Cancel`, onClick: onClose },
      ]}
      icon={Download}
      color="information"
      onClose={onClose}
      title={t`Export ${integrations.length} integrations`}
      visible={visible}
    >
      <WrapperWithLoading
        loading={downloading}
        description={<Trans>Preparing your export file</Trans>}
      >
        <Flex flexDirection="column" marginTop={1}>
          {error && <Alert intent="danger" marginBottom={1} title={error} />}
          <Alert
            intent="information"
            marginBottom={1}
            title={t`Location-level data is not included`}
          >
            <Trans>
              Only the integration definition is exported. Configured accounts
              (SRM), location-specific settings, credentials, and secret values
              stay in this environment and must be set up again after import.
            </Trans>
          </Alert>
          <Flex flexDirection="column" marginTop={1}>
            {integrations.map(({ id, attributes }) => (
              <IntegrationRow key={id}>
                <Flex flexDirection="column">
                  <P weight="semibold" marginBottom={0}>
                    {attributes.name}
                  </P>
                  <P size="small" color="alaskanHusky" marginBottom={0}>
                    {attributes.category} · {attributes.audience?.join(', ')}
                  </P>
                </Flex>
                <RemoveButton
                  type="button"
                  aria-label={t`Remove ${attributes.name} from export`}
                  onClick={() => onRemove(id)}
                >
                  <Times size="small" />
                </RemoveButton>
              </IntegrationRow>
            ))}
          </Flex>
        </Flex>
      </WrapperWithLoading>
    </Modal>
  );
}

ExportIntegrationsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  integrations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      // eslint-disable-next-line react/forbid-prop-types
      attributes: PropTypes.object.isRequired,
    }),
  ),
  onRemove: PropTypes.func.isRequired,
  onExported: PropTypes.func.isRequired,
};
